import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { Button, Input, checkEmptyRequiredFields } from '@web/utils';
import plusIcon from '@web/utils/src/assets/icons/functions/plus-icon.svg';
import { useCustomToast } from '@/hooks/useCustomToast';
import { Silo, SiloUpdateInput } from '@/types';
import {
  useCreateSiloMutation,
  useUpdateSiloMutation,
} from '../Silos.generated';

const siloInputFields = [
  {
    id: 'name',
    name: 'name',
    required: true,
  },
  {
    id: 'addressID',
    name: 'address',
  },
  {
    id: 'capacity',
    name: 'capacity',
    type: 'number',
    required: true,
  },
  { id: 'description', name: 'description', required: true },
  {
    id: 'icon',
    name: 'icon',
  },
];

export enum SiloTypes {
  FLAT = 'FLAT',
  HOPPER = 'HOPPER',
}

const SiloFormDefaultValues: SiloUpdateInput = {
  name: '',
  capacity: 0,
  currentProduce: '',
  freeCapacity: 0,
  description: '',
  icon: SiloTypes.FLAT,
};

export interface CreateOrEditSiloFormProps {
  setIsOpen: (isOpen: boolean) => void;
  isEdit?: boolean;
  silo?: Silo;
}

const CreateOrEditSiloForm = (props: CreateOrEditSiloFormProps) => {
  const { isEdit = false, silo, setIsOpen } = props;
  const { t } = useTranslation();
  const toast = useCustomToast();
  const [{ fetching }, createSiloMutation] = useCreateSiloMutation();
  const [_, updateSiloMutation] = useUpdateSiloMutation();
  const [siloForm, setSiloForm] = useState<SiloUpdateInput>(
    silo
      ? {
          name: silo.name,
          capacity: silo.capacity,
          currentProduce: silo.currentProduce,
          freeCapacity: silo.freeCapacity,
          description: silo.description,
          icon: silo.icon || SiloTypes.FLAT,
        }
      : SiloFormDefaultValues,
  );

  const handleInputChange = (name, value) => {
    setSiloForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelButton = () => {
    setSiloForm(SiloFormDefaultValues);
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    // check if any of the form fields that are required are empty
    const areAnyRequiredFieldsEmpty = checkEmptyRequiredFields(
      siloInputFields,
      siloForm,
    );

    if (areAnyRequiredFieldsEmpty.length > 0) {
      toast('error', t('farm.silos.fill_required_fields'));
      return;
    }

    if (siloForm.capacity < siloForm.freeCapacity) {
      toast('error', t('farm.silos.capacity_error'));
      return;
    }
    if (!isEdit) {
      try {
        const result = await createSiloMutation({
          silo: { ...siloForm, freeCapacity: siloForm.capacity },
        });
        if (result.data?.createSilo) {
          setSiloForm(SiloFormDefaultValues);
          toast('success', t('farm.silos.created_sucessfully'));
          setIsOpen(false);
        } else {
          toast('error', t('farm.silos.error'));
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      try {
        const result = await updateSiloMutation({
          silo: { ...siloForm, freeCapacity: siloForm.capacity },
          id: silo?.id || '1',
        });
        if (result.data?.updateSilo) {
          setSiloForm(SiloFormDefaultValues);
          toast('success', t('farm.silos.updated_sucessfully'));
          setIsOpen(false);
        } else {
          toast('error', t('farm.silos.error'));
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  return (
    <div className="bg-white rounded-8-0 p-2-0">
      <div className="flex justify-between">
        <p className="text-2-0 font-bd mb-2-0">
          {t('farm.silos.add_new_silo')}
        </p>
        <div
          className="rotate-45 cursor-pointer opacity-50 mr-1-0 "
          data-testid="close"
          onClick={handleCancelButton}
        >
          <ReactSVG src={plusIcon} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-y-2-0">
        {siloInputFields
          .filter((i) => !['addressID', 'icon'].includes(i.id))
          .map((item) => (
            <Input
              key={item.id}
              onChange={handleInputChange}
              label={t(`farm.silos.${item.name}` as any) ?? ''}
              id={item.id}
              type={item.type}
              step={item.type === 'number' ? 0.001 : undefined}
              required={item.required}
              disabled={fetching}
              value={siloForm[item.id]}
              data-testid={item.id}
            />
          ))}
      </div>
      <div className="mt-1-0">
        <Button onClick={handleSubmit} data-testid="submit-btn">
          {t('global.submit')}
        </Button>
      </div>
    </div>
  );
};
export default CreateOrEditSiloForm;
